/**
 * guides-search.ts -- shared KB content-search primitives (KB-2b, 18-Aug-2026).
 *
 * PO decision (18-Aug): ONE search in the platform -- the global cmd+K
 * overlay. The KB content index built at build time
 * (scripts/build-guides-index.mjs -> /guides-index.<lang>.json) is consumed
 * here so BOTH the overlay and any future surface share the same folding
 * and the same lazy loader instead of growing private copies.
 *
 * Everything stays client-side (PO law): one static JSON fetch per
 * language, cached in-module and by the browser; search is a pure string
 * scan in the user's tab. Zero server compute, zero D1.
 *
 * Lesson #215 note (module-level state vs Vite chunk splitting): if two
 * chunks inline this module, the worst case is ONE duplicate fetch of a
 * small static JSON already sitting in the browser HTTP cache -- an
 * idempotent read, not a correctness hazard, so the plain module cache is
 * acceptable here BY ANALYSIS, not by oversight.
 */

export type GuideIndexEntry = {
  path: string;
  title: string;
  headings: string[];
  text: string;
};

/**
 * Diacritics-insensitive fold -- "sabloane" finds "Șabloane".
 * NFD decomposition strips combining marks, including the comma-below
 * on Romanian s/t (U+0326 sits inside the U+0300-036F combining range).
 */
export const fold = (s: string): string =>
  s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const cache: Partial<Record<"ro" | "en", GuideIndexEntry[] | null>> = {};
const inflight: Partial<
  Record<"ro" | "en", Promise<GuideIndexEntry[] | null>>
> = {};

/**
 * Lazy, cached loader for the build-time content index. Resolves to null
 * on ANY failure (missing file in vite dev, network hiccup) -- callers
 * degrade to title/description matching and the UI never breaks.
 */
export function loadGuidesIndex(
  langRaw: string,
): Promise<GuideIndexEntry[] | null> {
  const lang: "ro" | "en" = langRaw === "ro" ? "ro" : "en";
  if (cache[lang] !== undefined) return Promise.resolve(cache[lang] ?? null);
  const pending = inflight[lang];
  if (pending) return pending;

  const p = fetch(`/guides-index.${lang}.json`)
    .then((r) => (r.ok ? (r.json() as Promise<GuideIndexEntry[]>) : null))
    .catch(() => null)
    .then((d) => {
      cache[lang] = d;
      delete inflight[lang];
      return d;
    });
  inflight[lang] = p;
  return p;
}

/** True when the already-folded needle occurs anywhere in the entry. */
export function guideContentMatches(
  entry: GuideIndexEntry,
  foldedNeedle: string,
): boolean {
  return fold(
    entry.title + " " + entry.headings.join(" ") + " " + entry.text,
  ).includes(foldedNeedle);
}
